import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowLeftRight, Check, ChevronDown, ChevronLeft, ChevronUp, Loader2, TriangleAlert, X } from "lucide-react";
import { Link } from "react-router-dom";
import { ActionButton, SecondaryAction } from "../kit";
import LoadFailedCard from "./LoadFailedCard";
import useCoachGame from "../../hooks/useCoachGame";
import { saveDay, gameDateLabel } from "../../lib/coachGames";
import useSaveBeforeLeave from "../../lib/useSaveBeforeLeave";
import { squadFiguresLine } from "../../lib/pickXiFigures";
import { fetchPlayerSummaries } from "../../lib/squadAverage";
import * as day from "../../lib/pickXiDay";

const GAMES_PATH = "/coach-portal/games";

/**
 * Issue #745 (Pick XI coach, On the day; D297, D302, D309, D316 Q3/Q7) — On the
 * day, drawn in `PickXIOnTheDay.dc.html` (round 13, Martin's round-12 comment:
 * "add the 12th man at the bottom of the player list"): today's batting and
 * bowling order, a player swapped out with a reason (unwell or injured), the
 * 12th man at the foot of the list, and the planned team kept as published.
 *
 * A published game opens here from its date (`CoachTeam`, `opensOnTheDay`).
 * The state is `lib/pickXiDay.js`; this file only draws it. It starts from the
 * day sheet when one was saved, otherwise from the published team. Planned
 * team is the published sheet, read only, and no change made here reaches it
 * (the API writes the day beside the published sheet, #1314). Players never see
 * today's changes (D316 Q7).
 *
 * Swap, on a row or the 12th man, opens a sheet over the team: Why (Unwell or
 * Injured, required), Who comes in (the 12th man first, then the bench), and
 * Make the swap. Change the order shows up and down in place on each row and
 * presses the button (stop point S2: no words the board does not draw). The
 * summary line for two or more changes joins the clauses with "; " (S3).
 *
 * States, each its own (D53): `loading`, `failed` (Try again), the team, and
 * `day-saved` / `day-save-failed`. A failed save keeps every change (D57).
 * Every in-place control (the switch, Change the order, the arrows, the
 * choices in the sheet) is marked in place, so G1 counts only the places to
 * go (D253).
 */
export default function OnTheDay({ gameId, source = null, besideList }) {
  const own = useCoachGame(gameId, { skip: !!source });
  const src = source || own;
  const [state, setState] = useState(() => (src.status === "ready" ? day.fromGame(src.game) : null));
  const seeded = useRef(src.status === "ready" ? src.game : null);
  const [summaries, setSummaries] = useState(null); // { [playerId]: { ok, data } } | null while loading
  const [view, setView] = useState("today"); // today | planned
  const [reorder, setReorder] = useState({ bat: false, bowl: false });
  const [swapping, setSwapping] = useState(null); // null | { n } for a batting place, { twelfth: true } for the 12th man
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | failed
  const swapControl = useRef(null); // the control that opened the sheet, for focus on close
  const [leaveFailed, setLeaveFailed] = useState(0); // counts failed leave-saves, to bring the failed card into view
  const stateRef = useRef(state);
  stateRef.current = state;
  // D331 (D57): leaving with changes that were not saved saves them first. Your games, the ribbon, the top
  // bar and any other link inside the app, the controls marked `data-leave` (sign out, the demo switch, the
  // role switch) and a reload all save the day first, as Game plan & notes does (#1318 F1, #1344). If that
  // save fails the coach stays here with the failed card and every change. An untouched sheet, a modified
  // click (a new tab) and an address outside the app are left to the browser. The saving state and a failed
  // card live on the day view, so a leave from Planned team goes back to it (review F1). A link is followed
  // only when the server has the sheet as it is now: an edit made during the save keeps the coach here (review F2).
  const leave = useSaveBeforeLeave({ save: () => send("idle"), beforeLeave: () => setView("today"), onLeaveFailed: () => setLeaveFailed((n) => n + 1) });
  const { edited, reset } = leave;
  // A coach who pressed Sign out from the top bar must see why nothing happened: the failed card sits below the order.
  useEffect(() => {
    if (leaveFailed) document.querySelector('[data-testid="xi-day-failed"]')?.scrollIntoView?.({ block: "nearest" });
  }, [leaveFailed]);

  useEffect(() => {
    if (src.status !== "ready") {
      setSummaries(null);
      return undefined;
    }
    let live = true;
    if (seeded.current !== src.game) {
      seeded.current = src.game;
      setState(day.fromGame(src.game));
      setSaveState("idle");
      reset();
    }
    setSummaries(null);
    // Each player's figures, 4 at a time, never blocking the rows.
    fetchPlayerSummaries((src.game?.squad || []).map((m) => m.player_id)).then((results) => {
      if (!live) return;
      const byId = {};
      for (const res of results) byId[res.playerId] = res;
      setSummaries(byId);
    });
    return () => {
      live = false;
    };
  }, [src.status, src.game, reset]);

  // One reducer call, applied to the latest state. Any edit takes a saved or failed notice down:
  // it describes what was sent before the edit.
  const act = useCallback((fn, ...args) => {
    edited();
    setState((s) => fn(s, ...args));
    setSaveState((v) => (v === "saved" || v === "failed" ? "idle" : v));
  }, [edited]);

  // One save, for the Save button and for leaving. True when the server has the sheet as it is now.
  const send = async (done) => {
    setSaveState("saving");
    try {
      await saveDay(gameId, day.toBody(stateRef.current));
      setSaveState(done);
    } catch (e) {
      console.error("OnTheDay: failed to save today's changes", e);
      setSaveState("failed");
      throw e;
    }
  };
  const save = () => leave.persist(() => send("saved"));

  const back = (
    <SecondaryAction as={Link} to={GAMES_PATH} variant="link" data-testid="xi-day-back" style={{ display: "flex", width: "fit-content", alignItems: "center", gap: 4 }}>
      <ChevronLeft size={16} aria-hidden="true" />
      Your games
    </SecondaryAction>
  );
  // One stable place for the way back: the same element at the same position across loading,
  // failed and ready (#1318 review F2).
  let body;
  if (src.status !== "ready" || !state) {
    body =
      src.status === "failed" ? (
        <LoadFailedCard title="Couldn't load the team" testId="xi-day-load-failed" retryTestId="xi-day-load-failed-retry" onRetry={src.reload} />
      ) : (
        <Skeleton />
      );
  } else {
    body = (
      <Team
        game={src.game}
        state={state}
        summaries={summaries}
        view={view}
        onView={setView}
        reorder={reorder}
        onReorder={(key) => setReorder((r) => ({ ...r, [key]: !r[key] }))}
        act={act}
        onSwap={(target, el) => {
          swapControl.current = el;
          setSwapping(target);
        }}
        saveState={saveState}
        onSave={save}
      />
    );
  }

  return (
    <div className="flex flex-col" style={{ gap: 14, padding: "var(--nw-space-page)" }}>
      {/* #1430: Your games is beside this sheet, so the way back to it is not drawn. */}
      {!besideList && back}
      {body}
      {src.status === "ready" && state && swapping && (
        <SwapSheet
          state={state}
          target={swapping}
          summaries={summaries}
          onClose={() => setSwapping(null)}
          onCloseFocus={() => swapControl.current?.focus()}
          onConfirm={(inId, reason) => {
            if (swapping.twelfth) act(day.swapTwelfth, inId);
            else act(day.swapOut, swapping.n, inId, reason);
            setSwapping(null);
          }}
        />
      )}
    </div>
  );
}

function Skeleton() {
  const bar = (w, c, h = 12) => <div style={{ width: w, height: h, borderRadius: h / 2, background: c }} />;
  return (
    <div data-testid="xi-day-loading" aria-busy="true" className="flex flex-col" style={{ gap: 6 }}>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 14px", height: 54, boxSizing: "border-box", borderRadius: "var(--nw-radius-card)", background: "var(--nw-surface)", border: "1px solid var(--nw-line)" }}>
          {bar("50%", "var(--nw-line)")}
          {bar("30%", "var(--nw-line-faint)", 10)}
        </div>
      ))}
    </div>
  );
}

function Team({ game, state, summaries, view, onView, reorder, onReorder, act, onSwap, saveState, onSave }) {
  const date = gameDateLabel(game.match_date);
  const figures = (id) => (summaries ? summaries[id] : null);
  const summary = day.summaryLine(state);
  const saving = saveState === "saving";

  return (
    <div data-testid="xi-day" className="flex flex-col" style={{ gap: 14 }}>
      <div className="flex flex-col" style={{ gap: 4 }}>
        <div className="nw-eyebrow">Pick the XI</div>
        <div className="nw-title" data-testid="xi-day-title">On the day</div>
        <div className="nw-lede">{`vs ${game.opponent} · ${date}`}</div>
      </div>

      <ViewSwitch view={view} onView={onView} />

      {view === "planned" ? <Planned game={game} date={date} figures={figures} /> : <Today state={state} figures={figures} summary={summary} reorder={reorder} onReorder={onReorder} act={act} onSwap={onSwap} />}

      {view === "today" && (
        <>
          {saveState === "saved" && (
            <div data-testid="xi-day-saved" role="status" style={cardStyle("var(--nw-navy)")}>
              <Check size={16} color="var(--nw-navy)" strokeWidth={2.5} aria-hidden="true" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, color: "var(--nw-ink)" }}>{`Saved against ${game.opponent} on ${date}. The planned team is kept as published.`}</span>
            </div>
          )}
          {saveState === "failed" && (
            <div data-testid="xi-day-failed" role="alert" style={{ ...cardStyle("var(--nw-alert)"), alignItems: "flex-start" }}>
              <TriangleAlert size={16} color="var(--nw-alert)" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
              <div className="flex flex-col" style={{ gap: 2 }}>
                <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, color: "var(--nw-ink)" }}>Couldn't save today's changes</span>
                <span className="nw-help">The swap and the new order are still here. Try again.</span>
              </div>
            </div>
          )}
          <ActionButton
            data-testid="xi-day-save"
            onClick={onSave}
            aria-disabled={saving ? "true" : undefined}
            aria-busy={saving ? "true" : undefined}
            icon={saving ? <Loader2 size={18} style={{ color: "#475569" }} className="animate-spin" /> : null}
            style={saving ? { background: "#E2E8F0", color: "#475569", opacity: 1, cursor: "default" } : undefined}
          >
            {saving ? "Saving…" : "Save today's changes"}
          </ActionButton>
        </>
      )}
    </div>
  );
}

/** The board's card: a coloured left edge, a glyph (the caller's) and the words. */
function cardStyle(edge) {
  return { display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderLeft: `3px solid ${edge}`, borderRadius: "var(--nw-radius-control)" };
}

/** Planned team | On the day: a switch in place, two 44px segments (`xi-day-views`). */
function ViewSwitch({ view, onView }) {
  const seg = (key, label, testId, first) => {
    const on = view === key;
    return (
      <button
        key={key}
        type="button"
        data-testid={testId}
        data-nav="in-place"
        aria-pressed={on}
        onClick={() => onView(key)}
        style={{
          flex: "1 1 0",
          minWidth: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 44,
          boxSizing: "border-box",
          padding: "6px 10px",
          border: "none",
          borderLeft: first ? "none" : "1px solid var(--nw-line)",
          fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.25,
          textAlign: "center",
          cursor: "pointer",
          background: on ? "var(--nw-navy)" : "var(--nw-surface)",
          color: on ? "#FFFFFF" : "var(--nw-ink-lede)",
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div data-testid="xi-day-views" role="group" style={{ display: "flex", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)", overflow: "hidden" }}>
      {seg("planned", "Planned team", "xi-view-planned", true)}
      {seg("today", "On the day", "xi-view-today", false)}
    </div>
  );
}

/** Today's working team: the summary, the batting order with the 12th man last, the bowling order. */
function Today({ state, figures, summary, reorder, onReorder, act, onSwap }) {
  const n = state.xi.length;
  return (
    <>
      {summary && (
        <div data-testid="xi-day-summary" role="status" style={cardStyle("var(--nw-navy)")}>
          <ArrowLeftRight size={16} color="var(--nw-navy)" strokeWidth={2.5} aria-hidden="true" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, color: "var(--nw-ink)" }}>{summary}</span>
        </div>
      )}

      <SectionHead title="Batting order" help="Today's order. Swap takes a player out and puts the 12th man or a bench player in." />
      <ReorderButton testId="xi-day-reorder-bat" pressed={reorder.bat} onClick={() => onReorder("bat")} />

      <div className="flex flex-col" style={{ gap: 6 }}>
        {state.xi.map((e, i) => {
          const place = i + 1;
          const name = day.nameOf(state, e.id);
          const res = figures(e.id);
          const swapped = !!(e.replaces && e.reason);
          return (
            // Keyed by place, so the swap control a sheet was opened from is still the same element when it closes.
            <Row key={place} testId={`xi-day-bat-${place}`} swapped={swapped}>
              <Badge>{place}</Badge>
              <Who name={name} chips={<TagChips captain={e.captain} vice={e.vice} keeper={e.keeper} />}>
                <PlayerFigures summary={res} />
                {swapped && <InForChip name={day.nameOf(state, e.replaces)} reason={e.reason} />}
              </Who>
              <div style={{ display: "flex", flexShrink: 0, alignItems: "center" }}>
                {reorder.bat && (
                  <>
                    <IconBtn testId={`xi-day-bat-up-${place}`} label={`Move ${name} up`} disabled={place === 1} onClick={() => act(day.moveUp, place)} icon={ChevronUp} />
                    <IconBtn testId={`xi-day-bat-down-${place}`} label={`Move ${name} down`} disabled={place === n} onClick={() => act(day.moveDown, place)} icon={ChevronDown} />
                  </>
                )}
                <SwapBtn testId={`xi-swap-${place}`} name={name} onClick={(el) => onSwap({ n: place }, el)} />
              </div>
            </Row>
          );
        })}
        {state.twelfth ? (
          <Row testId="xi-day-twelfth">
            <Badge tint>12</Badge>
            <Who name={day.nameOf(state, state.twelfth)}>
              <span className="nw-help">12th man · fields only</span>
            </Who>
            <SwapBtn testId="xi-day-twelfth-swap" name={day.nameOf(state, state.twelfth)} onClick={(el) => onSwap({ twelfth: true }, el)} />
          </Row>
        ) : (
          <div
            data-testid="xi-day-twelfth-empty"
            style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 56, padding: "8px 12px", boxSizing: "border-box", background: "var(--nw-page)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
          >
            <Badge muted>12</Badge>
            <span style={{ fontWeight: 700, fontSize: 15, color: "var(--nw-ink-faint)" }}>No 12th man today</span>
          </div>
        )}
      </div>

      <SectionHead title="Bowling order" help="Today's order. 1 and 2 open." />
      <ReorderButton testId="xi-day-reorder-bowl" pressed={reorder.bowl} onClick={() => onReorder("bowl")} />
      <div className="flex flex-col" style={{ gap: 6 }}>
        {state.bowling.map((id, i) => {
          const place = i + 1;
          const name = day.nameOf(state, id);
          const res = figures(id);
          const line = bowlingLine(res && res.ok ? res.data : null, place <= 2);
          return (
            <Row key={place} testId={`xi-day-bowl-${place}`} padding="8px 4px 8px 12px">
              <Badge round>{place}</Badge>
              <Who name={name}>{line && <span className="nw-help">{line}</span>}</Who>
              {reorder.bowl && (
                <div style={{ display: "flex", flexShrink: 0 }}>
                  <IconBtn testId={`xi-day-bowl-up-${place}`} label={`Move ${name} up the bowling order`} disabled={place === 1} onClick={() => act(day.moveBowler, place, -1)} icon={ChevronUp} />
                  <IconBtn testId={`xi-day-bowl-down-${place}`} label={`Move ${name} down the bowling order`} disabled={place === state.bowling.length} onClick={() => act(day.moveBowler, place, 1)} icon={ChevronDown} />
                </div>
              )}
            </Row>
          );
        })}
      </div>
    </>
  );
}

/** "Opening bowler · 26 ov · 11 wkts · econ 4.23": the board's bowling line, without a batting place. */
function bowlingLine(summary, opening) {
  const d = summary || {};
  const parts = [];
  if (opening) parts.push("Opening bowler");
  const overs = Number(d.total_overs) || 0;
  if (overs > 0) {
    const wkts = Number(d.total_wickets) || 0;
    parts.push(`${overs} ov`, `${wkts} ${wkts === 1 ? "wkt" : "wkts"}`);
    if (d.econ != null) parts.push(`econ ${Number(d.econ).toFixed(2)}`);
  }
  return parts.join(" · ");
}

/** Planned team: the team as published, read only. No swap, no reorder, no save. */
function Planned({ game, date, figures }) {
  const planned = day.planned(game);
  return (
    <div data-testid="xi-day-planned" className="flex flex-col" style={{ gap: 10 }}>
      <div className="flex flex-col" style={{ gap: 6 }}>
        {(planned?.rows || []).map((r) => (
          <Row key={r.n} testId={`xi-day-bat-${r.n}`} padding="8px 12px">
            <Badge>{r.n}</Badge>
            <Who name={r.name} chips={<TagChips captain={r.captain} vice={r.vice} keeper={r.keeper} />}>
              <PlayerFigures summary={figures(r.id)} />
            </Who>
          </Row>
        ))}
        {planned?.twelfth && (
          <Row testId="xi-day-twelfth" padding="8px 12px">
            <Badge tint>12</Badge>
            <Who name={planned.twelfth.name}>
              <span className="nw-help">12th man · fields only</span>
            </Who>
          </Row>
        )}
      </div>
      <div className="nw-help">{`The team as published on ${date}. Today's changes don't alter it.`}</div>
    </div>
  );
}

function SectionHead({ title, help }) {
  return (
    <div className="flex flex-col" style={{ gap: 2 }}>
      <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 20, letterSpacing: "-0.3px", color: "var(--nw-ink)" }}>{title}</div>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>{help}</div>
    </div>
  );
}

/** Change the order: pressed shows the up and down arrows on each row, in place. */
function ReorderButton({ testId, pressed, onClick }) {
  return (
    <button
      type="button"
      data-testid={testId}
      data-nav="in-place"
      aria-pressed={pressed}
      onClick={onClick}
      style={{
        alignSelf: "flex-start",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        minHeight: 44,
        padding: "0 14px",
        background: pressed ? "var(--nw-navy-tint)" : "var(--nw-surface)",
        border: `1px solid ${pressed ? "var(--nw-navy)" : "var(--nw-line-strong)"}`,
        borderRadius: "var(--nw-radius-control)",
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        fontSize: 14,
        fontWeight: 600,
        color: "var(--nw-navy)",
        cursor: "pointer",
      }}
    >
      <ArrowLeftRight size={16} strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0 }} />
      Change the order
    </button>
  );
}

/** One list row in the board's card: a navy edge when someone has come in. */
function Row({ testId, swapped = false, padding = "8px 8px 8px 12px", children }) {
  return (
    <div
      data-testid={testId}
      style={{ display: "flex", alignItems: "center", gap: 10, padding, background: "var(--nw-surface)", border: "1px solid", borderColor: swapped ? "var(--nw-navy)" : "var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
    >
      {children}
    </div>
  );
}

function Badge({ children, muted = false, tint = false, round = false }) {
  const base = { flexShrink: 0, width: 32, height: 32, boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700 };
  let look = { background: "var(--nw-navy)", color: "#FFFFFF", borderRadius: 8 };
  if (muted) look = { background: "var(--nw-line-faint)", color: "var(--nw-ink-faint)", borderRadius: 8 };
  if (tint) look = { background: "var(--nw-navy-tint)", color: "var(--nw-navy)", border: "1px solid var(--nw-navy)", borderRadius: 8 };
  if (round) look = { background: "var(--nw-surface)", color: "var(--nw-navy)", border: "2px solid var(--nw-navy)", borderRadius: "var(--nw-radius-pill)" };
  return <span style={{ ...base, ...look }}>{children}</span>;
}

function Who({ name, chips = null, children }) {
  return (
    <span style={{ display: "flex", flexDirection: "column", gap: 3, flexGrow: 1, minWidth: 0 }}>
      <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span className="nw-rowname" style={{ overflowWrap: "anywhere" }}>{name}</span>
        {chips}
      </span>
      {children}
    </span>
  );
}

/** C, VC and WK as they were published. Chips, not controls: tags are not edited on the day. */
function TagChips({ captain, vice, keeper }) {
  const chip = (style, text) => (
    <span style={{ display: "inline-flex", alignItems: "center", padding: "1px 6px", borderRadius: 6, fontSize: 11, fontWeight: 700, ...style }}>{text}</span>
  );
  return (
    <>
      {captain && chip({ background: "var(--nw-navy)", color: "#FFFFFF" }, "C")}
      {vice && chip({ background: "var(--nw-navy-tint)", color: "var(--nw-navy)", border: "1px solid var(--nw-navy)" }, "VC")}
      {keeper && chip({ background: "var(--nw-ok)", color: "#FFFFFF" }, "WK")}
    </>
  );
}

/** "In for [OUT] · unwell": the chip on a row whose player came in. */
function InForChip({ name, reason }) {
  return (
    <span style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", background: "var(--nw-navy-tint)", color: "var(--nw-navy)", fontSize: 11, fontWeight: 600, borderRadius: "var(--nw-radius-pill)", overflowWrap: "anywhere" }}>
      {`In for ${name} · ${reason}`}
    </span>
  );
}

/** A player's batting figures under their name: a skeleton while loading, a short note if the read failed. */
function PlayerFigures({ summary }) {
  if (!summary) return <span aria-hidden="true" style={{ display: "inline-block", height: 12, width: "55%", borderRadius: 6, background: "var(--nw-line)" }} />;
  if (!summary.ok) return <span className="nw-help">Couldn't load this player's figures</span>;
  return <span className="nw-help">{squadFiguresLine(summary.data)}</span>;
}

/** The 44px swap control on a row (`xi-swap-<n>`). It opens a sheet, so G1 counts it, once per kind. */
function SwapBtn({ testId, name, onClick }) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={`Swap ${name} out`}
      aria-haspopup="dialog"
      onClick={(e) => onClick(e.currentTarget)}
      style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, padding: 0, boxSizing: "border-box", background: "var(--nw-surface)", border: "1px solid var(--nw-line-strong)", borderRadius: "var(--nw-radius-control)", color: "var(--nw-navy)", cursor: "pointer" }}
    >
      <ArrowLeftRight size={16} strokeWidth={2} aria-hidden="true" />
    </button>
  );
}

/** Up or down: an icon in a 44px square that acts in place. An end of the list stays put (`aria-disabled`, still reachable). */
function IconBtn({ testId, label, icon: Icon, onClick, disabled = false }) {
  return (
    <button
      type="button"
      data-testid={testId}
      data-nav="in-place"
      aria-label={label}
      aria-disabled={disabled ? "true" : undefined}
      onClick={disabled ? undefined : onClick}
      style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, padding: 0, background: "transparent", border: "none", color: disabled ? "var(--nw-ink-faint)" : "var(--nw-ink-lede)", cursor: disabled ? "not-allowed" : "pointer" }}
    >
      <Icon size={20} strokeWidth={2} aria-hidden="true" />
    </button>
  );
}

/**
 * The swap sheet (`swap-open`): a dialog over the team. Focus moves into it on
 * open (Radix) and returns to the control it was opened from on close
 * (#959). Why is Unwell or Injured and is required before Make the swap works;
 * swapping the 12th man out asks no reason, because the API stores none for the
 * 12th seat. Judged on its own choices (Q-C2): Close and Make the swap.
 */
function SwapSheet({ state, target, summaries, onClose, onCloseFocus, onConfirm }) {
  const forTwelfth = !!target.twelfth;
  const outId = forTwelfth ? state.twelfth : state.xi[target.n - 1]?.id;
  const outName = day.nameOf(state, outId);
  const [reason, setReason] = useState(null);
  const [inId, setInId] = useState(null);
  const options = useMemo(() => day.incoming(state, { forTwelfth }), [state, forTwelfth]);
  const ready = !!inId && (forTwelfth || !!reason);
  // "The 12th man is listed first" only where he is on the list (review F5): not once he has come in, and not with none named.
  const listedFirst = !forTwelfth && options.some((o) => o.role === "twelfth");

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/80" />
        <Dialog.Content
          data-testid="xi-swap-sheet"
          {...(listedFirst ? {} : { "aria-describedby": undefined })}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            onCloseFocus();
          }}
          className="fixed z-50 inset-x-0 bottom-0"
          style={{
            background: "var(--nw-surface)",
            padding: "20px 20px 24px",
            maxHeight: "92vh",
            overflowY: "auto",
            maxWidth: 520,
            marginLeft: "auto",
            marginRight: "auto",
            borderTopLeftRadius: "var(--nw-radius-card)",
            borderTopRightRadius: "var(--nw-radius-card)",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <Dialog.Title style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 18, color: "var(--nw-ink)", overflowWrap: "anywhere" }}>{`Swap ${outName} out`}</Dialog.Title>
            <Dialog.Close
              data-testid="xi-swap-close"
              aria-label="Close"
              style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, boxSizing: "border-box", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)", color: "var(--nw-navy)", cursor: "pointer" }}
            >
              <X size={16} strokeWidth={2} aria-hidden="true" />
            </Dialog.Close>
          </div>

          {!forTwelfth && (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--nw-ink)" }} id="xi-swap-why">Why</div>
              <div data-testid="xi-swap-reason" role="radiogroup" aria-labelledby="xi-swap-why" style={{ display: "flex", gap: 8 }}>
                {day.SWAP_REASONS.map((r) => {
                  const on = reason === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      data-testid={r.testId}
                      data-nav="in-place"
                      onClick={() => setReason(r.value)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        minHeight: 44,
                        padding: "0 16px",
                        boxSizing: "border-box",
                        borderRadius: "var(--nw-radius-pill)",
                        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                        background: on ? "var(--nw-navy)" : "var(--nw-surface)",
                        color: on ? "#FFFFFF" : "var(--nw-ink-lede)",
                        border: on ? "1px solid var(--nw-navy)" : "1px solid var(--nw-line-strong)",
                      }}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--nw-ink)" }} id="xi-swap-who">Who comes in</div>
          <div role="radiogroup" aria-labelledby="xi-swap-who" className="flex flex-col" style={{ gap: 6 }}>
            {options.map((o) => {
              const on = inId === o.id;
              const res = summaries ? summaries[o.id] : null;
              const figures = !res ? "" : res.ok ? squadFiguresLine(res.data) : "Couldn't load this player's figures";
              const line = [o.role === "twelfth" ? "12th man" : "Bench", figures].filter(Boolean).join(" · ");
              return (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  aria-label={`Bring in ${o.name}`}
                  data-testid={`xi-in-${o.id}`}
                  data-nav="in-place"
                  onClick={() => setInId(o.id)}
                  style={{ display: "flex", alignItems: "center", gap: 4, minHeight: 56, boxSizing: "border-box", padding: "6px 4px 6px 14px", width: "100%", textAlign: "left", background: "var(--nw-surface)", border: `1px solid ${on ? "var(--nw-navy)" : "var(--nw-line)"}`, borderRadius: "var(--nw-radius-card)", cursor: "pointer" }}
                >
                  <span style={{ display: "flex", flexDirection: "column", gap: 2, flexGrow: 1, minWidth: 0 }}>
                    <span className="nw-rowname" style={{ overflowWrap: "anywhere" }}>{o.name}</span>
                    <span className="nw-help">{line}</span>
                  </span>
                  <span aria-hidden="true" style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44 }}>
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        boxSizing: "border-box",
                        borderRadius: 6,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: on ? "var(--nw-navy)" : "var(--nw-surface)",
                        border: on ? "none" : "2px solid var(--nw-line-strong)",
                      }}
                    >
                      {on && <Check size={14} strokeWidth={3} color="#FFFFFF" aria-hidden="true" />}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {listedFirst && (
            <Dialog.Description style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
              {`The 12th man is listed first. Whoever comes in takes ${outName}'s place in the batting order; change the order after if you need to.`}
            </Dialog.Description>
          )}

          <ActionButton
            data-testid="xi-swap-confirm"
            onClick={ready ? () => onConfirm(inId, reason) : undefined}
            aria-disabled={ready ? undefined : "true"}
            style={ready ? undefined : { opacity: 0.6, cursor: "not-allowed" }}
          >
            Make the swap
          </ActionButton>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
